import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  InputAdornment,
  Breadcrumbs,
  Link,
  Stack,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Tooltip
} from '@mui/material';
import {
  BusinessCenter,
  Search,
  Visibility,
  Delete,
  Email,
  NavigateNext,
  Home,
  FileDownload,
  PersonAdd,
  Groups,
  LocationOn,
  FilterAlt,
  RestartAlt
} from '@mui/icons-material';
import { db } from '../../config/firebase';
import { collection, getDocs, deleteDoc, doc, query, where, Timestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { PartnerFormData, ServiceType, TeamSize, SERVICE_OPTIONS, TEAM_SIZE_OPTIONS, PackageType } from '../../types/partner';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import { GridContainer, GridItem } from '../common/GridWrapper';
import emailService from '../../utils/emailService';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// Helper function to convert Firestore timestamp to JS Date
const convertTimestamp = (timestamp: any): Date => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  } else if (timestamp instanceof Date) {
    return timestamp;
  } else if (timestamp && timestamp.seconds) {
    return new Date(timestamp.seconds * 1000);
  }
  return new Date();
};

// Thay thế hàm exportToCSV bằng hàm exportToExcel
const exportToExcel = async (partners: PartnerFormData[]) => {
  try {
    // Tạo một workbook mới
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Danh sách đối tác');
    
    // Thiết lập tiêu đề cột đúng tiếng Việt
    worksheet.columns = [
      { header: 'Email', key: 'email', width: 30 },
      { header: 'Tên doanh nghiệp', key: 'businessName', width: 25 },
      { header: 'Dịch vụ', key: 'services', width: 25 },
      { header: 'Quy mô đội ngũ', key: 'teamSize', width: 15 },
      { header: 'Địa chỉ', key: 'address', width: 35 },
      { header: 'Gói đăng ký', key: 'selectedPackage', width: 15 },
      { header: 'Ngày đăng ký', key: 'createdAt', width: 20 },
      { header: 'Trạng thái', key: 'status', width: 15 },
    ];
    
    // Định dạng tiêu đề cột
    worksheet.getRow(1).font = { bold: true, size: 12 };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE91E63' } // Màu hồng của Bookdi
    };
    worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
    worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } }; // Chữ màu trắng
    
    // Thêm dữ liệu từ danh sách partners
    partners.forEach(partner => {
      const createdAtFormatted = partner.createdAt instanceof Date
        ? format(partner.createdAt, 'dd/MM/yyyy - HH:mm')
        : format(convertTimestamp(partner.createdAt), 'dd/MM/yyyy - HH:mm');
        
      worksheet.addRow({
        email: partner.email,
        businessName: partner.businessName,
        services: (partner.services || []).map(s => getServiceLabel(s)).join(', '),
        teamSize: TEAM_SIZE_OPTIONS.find(option => option.value === partner.teamSize)?.label || partner.teamSize,
        address: partner.hasNoFixedAddress ? 'Không có địa chỉ cố định' : partner.address,
        selectedPackage: partner.selectedPackage === 'free' ? 'Miễn Phí' : 'Premium',
        createdAt: createdAtFormatted,
        status: partner.status || 'Đang chờ'
      });
    });
    
    // Auto-fit columns
    worksheet.columns.forEach(column => {
      column.width = Math.max(column.width || 12, 12);
    });
    
    // Đặt border cho toàn bộ bảng
    worksheet.eachRow((row, rowNumber) => {
      row.eachCell({ includeEmpty: true }, cell => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        
        // Căn giữa ô đầu tiên (Email)
        if (rowNumber > 1) {
          cell.alignment = { vertical: 'middle' };
        }
      });
    });

    // Đặt màu nền cho các hàng xen kẽ
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1 && rowNumber % 2 === 0) {
        row.eachCell({ includeEmpty: true }, cell => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF9F9F9' }
          };
        });
      }
    });
    
    // Chuyển đổi sang buffer và tạo file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `Danh_sach_doi_tac_${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
    
    toast.success('Xuất file Excel thành công');
  } catch (error) {
    console.error('Lỗi khi xuất Excel:', error);
    toast.error('Không thể xuất file Excel');
  }
};

// Format service name for display
const getServiceLabel = (serviceValue: string): string => {
  const service = SERVICE_OPTIONS.find(s => s.value === serviceValue);
  return service ? service.label : serviceValue;
};

const PartnerAdmin: React.FC = () => {
  const [partners, setPartners] = useState<PartnerFormData[]>([]);
  const [filteredPartners, setFilteredPartners] = useState<PartnerFormData[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedServices, setSelectedServices] = useState<ServiceType[]>([]);
  const [selectedTeamSizes, setSelectedTeamSizes] = useState<TeamSize[]>([]);
  const [selectedPackages, setSelectedPackages] = useState<PackageType[]>([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<PartnerFormData | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState<string | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    newThisWeek: 0,
    noFixedAddress: 0
  });

  // Fetch partners from Firestore
  const fetchPartners = async () => {
    try {
      setLoading(true);
      const partnersCollection = collection(db, 'partners');
      const partnersSnapshot = await getDocs(partnersCollection);
      
      const partnersData = partnersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as unknown as PartnerFormData[]; // Cast to unknown first to fix TypeScript error
      
      setPartners(partnersData);
      setFilteredPartners(partnersData);
      
      // Calculate stats
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      
      setStats({
        total: partnersData.length,
        newThisWeek: partnersData.filter(p => {
          const createdAt = convertTimestamp(p.createdAt);
          return createdAt >= oneWeekAgo;
        }).length,
        noFixedAddress: partnersData.filter(p => p.hasNoFixedAddress).length
      });
      
    } catch (error) {
      console.error('Error fetching partners:', error);
      toast.error('Không thể tải danh sách đối tác');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  // Apply filters when search term or filters change
  useEffect(() => {
    let results = [...partners];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      results = results.filter(
        partner => 
          partner.email.toLowerCase().includes(term) || 
          partner.businessName.toLowerCase().includes(term)
      );
    }
    
    // Apply service filter
    if (selectedServices.length > 0) {
      results = results.filter(
        partner => partner.services.some(service => 
          selectedServices.includes(service)
        )
      );
    }
    
    // Apply team size filter
    if (selectedTeamSizes.length > 0) {
      results = results.filter(
        partner => selectedTeamSizes.includes(partner.teamSize)
      );
    }
    
    // Apply package filter
    if (selectedPackages.length > 0) {
      results = results.filter(
        partner => selectedPackages.includes(partner.selectedPackage)
      );
    }
    
    setFilteredPartners(results);
    setPage(0);
  }, [searchTerm, selectedServices, selectedTeamSizes, selectedPackages, partners]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResendEmail = async (email: string) => {
    if (!emailService.isValidEmail(email)) {
      toast.error(`Email không hợp lệ: ${email}`);
      return;
    }
    
    try {
      await emailService.sendPasswordResetEmail(email);
      toast.success(`Đã gửi email đặt lại mật khẩu đến ${email}`);
    } catch (error) {
      console.error('Error sending verification email:', error);
      toast.error('Không thể gửi email xác nhận. Vui lòng thử lại sau.');
    }
  };

  const handleSendWelcomeEmail = async (email: string, businessName: string) => {
    if (!emailService.isValidEmail(email)) {
      toast.error(`Email không hợp lệ: ${email}`);
      return;
    }
    
    try {
      await emailService.sendPartnerWelcomeEmail(email, businessName);
    } catch (error) {
      console.error('Error sending welcome email:', error);
      toast.error('Không thể gửi email chào mừng. Vui lòng thử lại sau.');
    }
  };

  const handleViewDetails = (partner: PartnerFormData) => {
    setSelectedPartner(partner);
    setDetailsOpen(true);
  };

  const handleDeleteClick = (partnerId: string) => {
    setPartnerToDelete(partnerId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!partnerToDelete) return;
    
    try {
      await deleteDoc(doc(db, 'partners', partnerToDelete));
      toast.success('Đã xoá đối tác thành công');
      fetchPartners(); // Refresh the list
    } catch (error) {
      console.error('Error deleting partner:', error);
      toast.error('Không thể xoá đối tác');
    } finally {
      setDeleteConfirmOpen(false);
      setPartnerToDelete(null);
    }
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedServices([]);
    setSelectedTeamSizes([]);
    setSelectedPackages([]);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <BusinessCenter sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
          <Typography variant="h4" fontWeight="bold">
            Quản lý đối tác
          </Typography>
        </Box>
        
        <Breadcrumbs 
          separator={<NavigateNext fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Link 
            color="inherit" 
            href="#" 
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Dashboard
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            Đối tác
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Stats Cards */}
      <GridContainer spacing={3} sx={{ mb: 4 }}>
        <GridItem xs={12} md={4}>
          <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Tổng số đối tác</Typography>
              <Typography variant="h3">{stats.total}</Typography>
            </CardContent>
          </Card>
        </GridItem>
        <GridItem xs={12} md={4}>
          <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Mới trong tuần</Typography>
              <Typography variant="h3">{stats.newThisWeek}</Typography>
            </CardContent>
          </Card>
        </GridItem>
        <GridItem xs={12} md={4}>
          <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Không có địa chỉ cố định</Typography>
              <Typography variant="h3">{stats.noFixedAddress}</Typography>
            </CardContent>
          </Card>
        </GridItem>
      </GridContainer>

      {/* Filters and Actions */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <GridContainer spacing={2} alignItems="center">
          <GridItem xs={12} md={3}>
            <TextField
              fullWidth
              label="Tìm kiếm theo email hoặc tên doanh nghiệp"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </GridItem>
          <GridItem xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Loại dịch vụ</InputLabel>
              <Select
                multiple
                value={selectedServices}
                onChange={(e) => setSelectedServices(e.target.value as ServiceType[])}
                input={<OutlinedInput label="Loại dịch vụ" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip 
                        key={value} 
                        label={getServiceLabel(value)} 
                        size="small" 
                      />
                    ))}
                  </Box>
                )}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterAlt />
                  </InputAdornment>
                }
              >
                {SERVICE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={selectedServices.indexOf(option.value as ServiceType) > -1} />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Quy mô đội ngũ</InputLabel>
              <Select
                multiple
                value={selectedTeamSizes}
                onChange={(e) => setSelectedTeamSizes(e.target.value as TeamSize[])}
                input={<OutlinedInput label="Quy mô đội ngũ" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip 
                        key={value} 
                        label={TEAM_SIZE_OPTIONS.find(option => option.value === value)?.label || value} 
                        size="small" 
                      />
                    ))}
                  </Box>
                )}
                startAdornment={
                  <InputAdornment position="start">
                    <Groups />
                  </InputAdornment>
                }
              >
                {TEAM_SIZE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={selectedTeamSizes.indexOf(option.value as TeamSize) > -1} />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Gói đăng ký</InputLabel>
              <Select
                multiple
                value={selectedPackages}
                onChange={(e) => setSelectedPackages(e.target.value as PackageType[])}
                input={<OutlinedInput label="Gói đăng ký" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip 
                        key={value} 
                        label={value === 'free' ? 'Miễn Phí' : 'Premium'} 
                        size="small" 
                        color={value === 'free' ? 'default' : 'primary'}
                      />
                    ))}
                  </Box>
                )}
              >
                <MenuItem value="free">
                  <Checkbox checked={selectedPackages.indexOf('free') > -1} />
                  <ListItemText primary="Miễn Phí" />
                </MenuItem>
                <MenuItem value="premium">
                  <Checkbox checked={selectedPackages.indexOf('premium') > -1} />
                  <ListItemText primary="Premium" />
                </MenuItem>
              </Select>
            </FormControl>
          </GridItem>
          <GridItem xs={12} md={3}>
            <Stack direction="row" spacing={1}>
              <Button 
                variant="outlined" 
                startIcon={<RestartAlt />} 
                onClick={handleResetFilters}
                fullWidth
              >
                Reset bộ lọc
              </Button>
              <Button 
                variant="contained" 
                startIcon={<FileDownload />} 
                onClick={() => exportToExcel(filteredPartners)}
                fullWidth
              >
                Xuất Excel
              </Button>
              <Button 
                variant="contained" 
                color="success" 
                startIcon={<PersonAdd />}
                fullWidth
              >
                Thêm đối tác
              </Button>
            </Stack>
          </GridItem>
        </GridContainer>
      </Paper>

      {/* Data Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : filteredPartners.length === 0 ? (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <Typography>Không tìm thấy đối tác nào</Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Tên doanh nghiệp</TableCell>
                    <TableCell>Dịch vụ đăng ký</TableCell>
                    <TableCell>Quy mô đội ngũ</TableCell>
                    <TableCell>Địa chỉ</TableCell>
                    <TableCell>Gói đăng ký</TableCell>
                    <TableCell>Ngày đăng ký</TableCell>
                    <TableCell align="center">Hành động</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPartners
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((partner) => (
                      <TableRow 
                        key={partner.id} 
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell>{partner.email}</TableCell>
                        <TableCell>{partner.businessName}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {partner.services.map((service) => (
                              <Chip 
                                key={service} 
                                label={getServiceLabel(service)}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {TEAM_SIZE_OPTIONS.find(option => option.value === partner.teamSize)?.label}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={partner.address || 'Không có địa chỉ cố định'}>
                            <Box sx={{ 
                              maxWidth: 200, 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {partner.hasNoFixedAddress ? (
                                <LocationOn color="error" fontSize="small" sx={{ mr: 0.5 }} />
                              ) : (
                                <LocationOn color="success" fontSize="small" sx={{ mr: 0.5 }} />
                              )}
                              {partner.address || 'Không có địa chỉ cố định'}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={partner.selectedPackage === 'free' ? 'Miễn Phí' : 'Premium'}
                            color={partner.selectedPackage === 'free' ? 'default' : 'primary'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {partner.createdAt instanceof Date 
                            ? format(partner.createdAt, 'dd/MM/yyyy - HH:mm', { locale: vi })
                            : format(convertTimestamp(partner.createdAt), 'dd/MM/yyyy - HH:mm', { locale: vi })}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Tooltip title="Xem chi tiết">
                              <IconButton 
                                size="small"
                                color="primary"
                                onClick={() => handleViewDetails(partner)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Gửi lại email xác nhận">
                              <IconButton 
                                size="small"
                                color="info"
                                onClick={() => handleResendEmail(partner.email)}
                              >
                                <Email />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Gửi email chào mừng">
                              <IconButton 
                                size="small"
                                color="success"
                                onClick={() => handleSendWelcomeEmail(partner.email, partner.businessName)}
                              >
                                <Email />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Xoá đối tác">
                              <IconButton 
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(partner.id as string)}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredPartners.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Số dòng mỗi trang:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} của ${count}`}
            />
          </>
        )}
      </Paper>

      {/* View Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          Chi tiết đối tác
        </DialogTitle>
        <DialogContent dividers>
          {selectedPartner && (
            <GridContainer spacing={2}>
              <GridItem xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Email</Typography>
                <Typography paragraph>{selectedPartner.email}</Typography>
              
                <Typography variant="subtitle1" fontWeight="bold">Tên doanh nghiệp</Typography>
                <Typography paragraph>{selectedPartner.businessName}</Typography>
              
                <Typography variant="subtitle1" fontWeight="bold">Website</Typography>
                <Typography paragraph>
                  {selectedPartner.website ? (
                    <Link href={selectedPartner.website} target="_blank" rel="noopener noreferrer">
                      {selectedPartner.website}
                    </Link>
                  ) : 'Không có'}
                </Typography>
              
                <Typography variant="subtitle1" fontWeight="bold">Quy mô đội ngũ</Typography>
                <Typography paragraph>
                  {TEAM_SIZE_OPTIONS.find(option => option.value === selectedPartner.teamSize)?.label}
                </Typography>
              </GridItem>
              
              <GridItem xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Dịch vụ đăng ký</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  {selectedPartner.services.map((service) => (
                    <Chip 
                      key={service} 
                      label={getServiceLabel(service)}
                      color="primary"
                    />
                  ))}
                </Box>
              
                <Typography variant="subtitle1" fontWeight="bold">Địa chỉ</Typography>
                <Typography paragraph>
                  {selectedPartner.hasNoFixedAddress ? 'Không có địa chỉ cố định' : selectedPartner.address}
                </Typography>
                
                {selectedPartner.latitude && selectedPartner.longitude && (
                  <>
                    <Typography variant="subtitle1" fontWeight="bold">Toạ độ</Typography>
                    <Typography paragraph>
                      {selectedPartner.latitude}, {selectedPartner.longitude}
                    </Typography>
                  </>
                )}
              
                <Typography variant="subtitle1" fontWeight="bold">Gói đăng ký</Typography>
                <Typography paragraph sx={{ textTransform: 'capitalize' }}>
                  {selectedPartner.selectedPackage === 'free' 
                    ? 'Gói Miễn Phí' 
                    : 'Gói Dịch Vụ'}
                </Typography>
              
                <Typography variant="subtitle1" fontWeight="bold">Ngày đăng ký</Typography>
                <Typography paragraph>
                  {selectedPartner.createdAt instanceof Date 
                    ? format(selectedPartner.createdAt, 'dd/MM/yyyy - HH:mm', { locale: vi })
                    : format(convertTimestamp(selectedPartner.createdAt), 'dd/MM/yyyy - HH:mm', { locale: vi })}
                </Typography>
              </GridItem>
            </GridContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Đóng</Button>
          {selectedPartner && (
            <Button 
              startIcon={<Email />}
              color="success"
              variant="contained"
              onClick={() => handleSendWelcomeEmail(selectedPartner.email, selectedPartner.businessName)}
            >
              Gửi email chào mừng
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Xác nhận xoá</DialogTitle>
        <DialogContent>
          <Typography>Bạn có chắc chắn muốn xoá đối tác này?</Typography>
          <Typography variant="caption" color="error">
            Hành động này không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Huỷ</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Xoá
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerAdmin; 